// === FILE HEADER: TileLayer.js ===
// Renders all static overlays (tiles, highlights, etc.) as grid children above the board.
// Animation/effect overlays are now handled by TileOverlayLayer.
// This component is part of the portal-based rendering refactor for Chess Slider.
// Last updated: 2025-07-29


import React, { useEffect } from 'react';
import Tile from './Tile';
import VortexIndicator from './VortexIndicator';
import pcModule from './TileLayer.pc.module.css';
import mobileModule from './TileLayer.mobile.module.css';
import useSelectedCssModule from '../hooks/useSelectedCssModule';
import { getMovableTileIdsByRules } from '../utils/gameUtils';
import { logDomEvent } from '../utils/spaLogger';

// COLOR_PIECE UNUSED

// Helper: convert file/rank (e.g., 'e4') to board-relative coordinates (0-7, 0-7)
function fileRankToCoords(fileRank) {
  const file = fileRank.charCodeAt(0) - 97; // 'a' = 0
  const rank = 8 - parseInt(fileRank[1], 10); // rank 8 = 0 (top)
  return { file, rank };
}



// Only static overlays should be rendered here. Animation/effect overlays must be passed to TileOverlayLayer.
function TileLayer({
  inPlaySquares,
  inPlayTiles,
  emptySquare,
  boardRect,
  squareSize,
  onTileClick,
  showSolvedIndicator,
  solveMovesActive,
  showMoveHints,
  getSolveMoveTileId,
  showFileRankLabels
}) {
  const styles = useSelectedCssModule(pcModule, mobileModule);
  const overlays = [];
  // Compute move hint tile IDs if enabled
  let moveHintTileIds = [];
  if (showMoveHints && inPlaySquares && inPlayTiles && emptySquare) {
    moveHintTileIds = getMovableTileIdsByRules(inPlaySquares, inPlayTiles, emptySquare);
  }
  // Render overlays for each square (8x8)
  for (let rank = 0; rank < 8; rank++) {
    for (let fileIdx = 0; fileIdx < 8; fileIdx++) {
      const fileChar = String.fromCharCode(97 + fileIdx); // 'a' = 0
      const fileRank = fileChar + (8 - rank);
      const tileId = inPlaySquares && inPlaySquares[fileRank] && inPlaySquares[fileRank].tileId;
      const tile = tileId && inPlayTiles && inPlayTiles[tileId];
      // Determine if this tile should show move hint
      const showMoveHint = tile && moveHintTileIds.includes(tile.tileId);
      // Determine if this tile is the next solve move
      const solveMoveTileId = solveMovesActive && getSolveMoveTileId ? getSolveMoveTileId() : null;
      const showSolveMoveHint = tile && solveMoveTileId && tile.tileId === solveMoveTileId;
      // Wrap each cell's overlays in a relative-positioned container
      overlays.push(
        <div
          key={`cell-${fileRank}`}
          style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
          data-grid-row={rank + 1}
          data-grid-column={fileIdx + 1}
        >
          {tile ? (
            <Tile
              fileRank={fileRank}
              tile={tile}
              onClick={onTileClick}
              highlight={showMoveHint}
              solveMoveHint={showSolveMoveHint}
              showSolvedIndicator={showSolvedIndicator}
            />
          ) : emptySquare === fileRank ? (
            <VortexIndicator
              fileRank={fileRank}
              className={styles.vortexIndicator}
              aria-hidden="true"
            />
          ) : (
            <div
              className={styles.emptySquare}
              style={{ zIndex: 10 }}
              aria-hidden="true"
            />
          )}
          {/* File/rank label rendered only if showFileRankLabels is true */}
          {showFileRankLabels && (
            <div
              className={styles.fileRankLabel}
              aria-hidden="true"
            >
              {fileRank}
            </div>
          )}
        </div>
      );
    }
  }

  // Optionally render file/rank labels if needed (not implemented here)
  // const fileRankLabels = ...

  return (
    <div className={styles.tileLayer}>
      {overlays}
      {/* {fileRankLabels} */}
    </div>
  );
}

export default TileLayer;
